<template>
  <div>
    <div class="tla-form-group">
      <label for="regNumber" class="tla-form-group__label" v-if="registrationLabel.length > 0">{{ registrationLabel }}</label>
      <div class="tla-registration" >
        <i class="tla-registration__icon">
          <img src="@/assets/images/widgets/euro-badge.svg"/>
        </i>
        <input
          class="tla-registration__input"
          type="text"
          id="regNumber"
          name="registration"
          v-model="valuationRequest.registration"
          placeholder="EJ03 OZF"
          v-validate.initial="'required'"
          data-vv-name="registration"
          :maxlength="8"
          :data-vv-scope="validationScope"
          @blur="validateComponent()"
          :class="getRegistrationInputValidationClass('registration', 'input')" />
        </div>
        <span class="tla-form-group__message" :class="getValidationClass('registration', 'message')">{{ errors.first(`${validationScope}.registration`) }}</span>
        <span v-if="showUnknownRegistrationError" class="tla-form-group__message tla-form-group__message--error">{{ unknownRegistrationErrorMessage }}</span>
        <span v-if="showErrorError" class="tla-form-group__message tla-form-group__message--error">{{ errorErrorMessage }}</span>
    </div>

    <div class="tla-form-group tla-form-group--spaced">
      <label for="currentMileage" class="tla-form-group__label tla-form-group__label--required">Current Mileage <span>*</span></label>
      <NumberInput
        id="currentMileage"
        name="currentMileage"
        v-model="valuationRequest.mileage"
        :min="0"
        :max="100000"
        :step="1000"
        :prefix="''"
        :suffix="' Miles'"
        :precision="0"
        :slider="true"
        :fieldName="'mileage'"
        v-validate.initial="'min_value:1'"
        data-vv-name="mileage"
        @mileage="valuationRequest.mileage = $event"
        :moneyValidationClass="getValidationClass('mileage', 'input', mileageChanged)"
        :data-vv-scope="validationScope">
       </NumberInput>
      <span class="tla-form-group__message" :class="getValidationClass('mileage', 'message', mileageChanged)">{{ errors.first(`${validationScope}.mileage`) }}</span>
      <vue-slider v-if="!hideSliders" v-model="valuationRequest.mileage" :min="0" :max="100000" :interval="1000" height="10" :dot-size="26" :tooltip="false"></vue-slider>
    </div>

    <button
      :disabled="invalid"
      class="tla-btn tla-btn--info tla-btn--spaced-top-md"
      type="button"
      @click="getVehicleValuationData()"
      name="vehicle-valuation-lookup-button"
      id="vehicle-valuation-lookup-button">
      {{nextButtonText}}
    </button>

    <button
      v-if="isOverlay"
      class="tla-btn tla-btn--link"
      @click="enterVehicleManually()"
      name="vehicle-valuation-manual-button"
      id="vehicle-valuation-manual-button">
      Enter vehicle manually
    </button>
  </div>
</template>

<script lang="ts">
import VeeValidate, {
  FieldError,
  Validator
} from 'vee-validate';
import {
  Component, Vue, Prop, Provide, Inject, Watch
} from 'vue-property-decorator';
import {
  State, Getter, Action, namespace
} from 'vuex-class';
import vueSlider from 'vue-slider-component';
import { vehicleValuationModule } from '@/store/modules/vehicle-valuation-store';
import ValuationRequest from '@/models/valuation/ValuationRequest';
import RegistrationLookupValidationMessages from '@/validation/widgets/registration-lookup';
import ValuationResultType from '@/enums/valuation/ValuationResultType';
import { validationService } from '@/services/ValidationService';
import NumberInput from '@/components/widgets/common/NumberInput.vue';
import ValuationResponse from '@/models/valuation/ValuationResponse';
import mobileCheckService from '@/services/MobileCheckService';

const VehicleValuationModule = namespace('vehicleValuationModule');

const EnquiryModule = namespace('enquiryModule');

Vue.use(VeeValidate, RegistrationLookupValidationMessages);

@Component({
  components: {
  vueSlider,
  NumberInput
  }
  })
export default class RegistrationLookupDetailsComponent extends Vue {
  @VehicleValuationModule.Action public retrieveVehicleValuationData;

  @VehicleValuationModule.Getter public valuationRequest;

  @EnquiryModule.Action public submitNextStep;

  @EnquiryModule.Getter public currentStep;

  @Inject('$validator') public $validator!: Validator;

  @Prop({ default: false })
  public isOverlay!: boolean;

  @Prop({ default: 'Next' })
  public nextButtonText!: string;

  @Prop({ default: 'registrationLookup' })
  public validationScope!: string;

  @Prop({ default: 'Enter Registration Number' })
  public registrationLabel!: string;

  @Prop({ default: 'Sorry, we don\'t recognise this plate. Please review your selection.' })
  public unknownRegistrationErrorMessage!: string;

  @Prop({ default: 'Sorry, an error occured. Please try again or use the manaual vehicle entry below.' })
  public errorErrorMessage!: string;

  private resultType!: ValuationResultType;

  private mileageChanged: boolean = false;

  private showUnknownRegistrationError: boolean = false;

  private showErrorError: boolean = false;
  
  private hideSliders: boolean = false;

  @Watch('valuationRequest.mileage')
  public mileage(newVal: number, oldVal: number) {
    this.mileageChanged = oldVal !== newVal;
  }

  public getRegistrationInputValidationClass(fieldName: string, fieldType?: string, fieldChanged?: boolean) {
    if (this.showUnknownRegistrationError || this.showErrorError) {
      return 'tla-form-group__input--error';
    }

    return validationService.getValidationClass(this.$validator, fieldName, fieldType, fieldChanged, false, this.validationScope);
  }

  public getValidationClass(fieldName: string, fieldType?: string, fieldChanged?: boolean) {
    return validationService.getValidationClass(this.$validator, fieldName, fieldType, fieldChanged, false, this.validationScope);
  }

  @Watch('valuationRequest.registration')
  public registration(newVal: number, oldVal: number) {
    if (oldVal !== newVal) {
      this.showUnknownRegistrationError = false;
      this.showErrorError = false;
    }
  }

  public getVehicleValuationData() {
    this.$emit('showLoader', true);
    this.retrieveVehicleValuationData(this.valuationRequest)
      .then((value) => {
        this.resultType = value;
        this.$emit('showLoader', false);
        this.setValuationResults(value);
      });
  }

  public get currentOverlayStep() {
    return this.$store.getters['enquiryModule/currentOverlayStep'];
  }

  public setValuationResults(value: ValuationResultType) {
    if (value === ValuationResultType.Successful) {
      if (this.isOverlay) {
        this.$store.dispatch('vehicleValuationModule/submitIsManualVehicle', false);
        this.$store.dispatch('enquiryModule/submitOverlayStep', this.currentOverlayStep + 1);
      } else {
        this.submitNextStep(this.currentStep + 1);
      }
    } else if (value === ValuationResultType.VehicleNotFound) {
      this.showUnknownRegistrationError = true;
    } else if (value === ValuationResultType.Error) {
      this.showErrorError = true;
    }
  }

  public enterVehicleManually() {
    this.$emit('createManualVehicleModules', this.currentOverlayStep + 2);
  }

  public validateComponent() {
    this.$validator.validateAll(this.validationScope);
  }

  public get invalid(): boolean {
    return this.$validator.errors.items.filter((x) => x.scope === this.validationScope).length > 0;
  }

  public created() {
    this.$validator.localize('en', RegistrationLookupValidationMessages.english);
    this.hideSliders = mobileCheckService.hideSliders;
  }
}
</script>
